import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {Link} from 'react-router';

import ElapsedTime from 'app/components/general/ElapsedTime';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {DocumentIcon} from 'app/notifly/components/DocumentIconWithNotifications';
import {useSignoffRequestActivities} from 'app/signoffs/api';
import {normalizeSignoffData} from 'app/signoffs/utils';
import {useTeams} from 'app/teams/api';
import {getSignoffUrlForTeam} from 'app/urls';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {SignoffStatus} from './SignoffStatus';

import {convertLocationParamsToFetchParams} from '../../teams/utils';

import {
  SignoffDocumentSerializedType,
  SignoffSerializedType,
  SignoffTriggerType,
  TeamSerializedType,
} from 'sweet-types';

import type {NormalizedSignoffDataType} from 'app/signoffs/types';

interface Props {
  isOpen: boolean;
  signoffData: NormalizedSignoffDataType;
  onClose: () => unknown;
  onClosed?: () => unknown;
}

export function SignoffActivitiesModal({
  isOpen,
  onClose,
  onClosed,
  signoffData,
}: Props) {
  const {data: activities, isError} = useSignoffRequestActivities({
    signoff_id: signoffData.signoffId,
  });

  const teamsIds: Array<number> = React.useMemo(
    function extractTeamsIds() {
      let teamsIds: Array<number> = [];

      for (const activity of activities?.results ?? []) {
        const activityTeamsIds = activity.additional_data.team_ids;

        if (activityTeamsIds != null) {
          for (const teamId of activityTeamsIds) {
            if (teamsIds.indexOf(teamId) === -1) {
              teamsIds.push(teamId);
            }
          }
        }
      }

      return teamsIds;
    },
    [activities?.results]
  );

  const {data: teamsList, isLoading: isLoadingTeams} = useTeams(
    teamsIds.length > 0
      ? convertLocationParamsToFetchParams({id: teamsIds.join(',')})
      : skipToken
  );

  const teamsMap: Record<number, TeamSerializedType> | null =
    React.useMemo(() => {
      if (teamsList == null && isLoadingTeams) {
        return null;
      }

      const map: Record<number, TeamSerializedType> = {};

      for (const team of teamsList?.results || []) {
        map[team.id] = team;
      }

      return map;
    }, [teamsList, isLoadingTeams]);

  return (
    <TabbedModal
      show={isOpen}
      onCancel={onClose}
      onClosed={onClosed}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Sign off Request'),
          icon: 'fa fa-signature',
          content: (
            <>
              <HeaderLine label={gt('Request for')} className="pb-2">
                {signoffData.user.name}
              </HeaderLine>
              <HeaderLine label={gt('To sign')} className="pb-2 border-bottom">
                <DocumentIcon
                  className="mr-1"
                  contentType={signoffData.documentContentType}
                />
                {getObjectName(
                  signoffData.documentName,
                  signoffData.documentContentType,
                  true
                )}
              </HeaderLine>
              <HeaderLine label={gt('Status')} className="py-2 border-bottom">
                <SignoffStatus
                  signoffData={signoffData}
                  previousVersionStyle="link-on-text"
                  alignment="left"
                />
              </HeaderLine>
              <div>
                <div className="mt-4 mb-1">{gt('Request reason')}</div>
                <ul className="list-group mb-2">
                  {teamsMap == null || (activities == null && !isError) ? (
                    <li className="list-group-item">
                      <p className="lead text-muted text-center mt-3">
                        <i className="fa fa-circle-notch fa-spin" />
                      </p>
                    </li>
                  ) : activities != null ? (
                    activities.count === 0 ? (
                      <li className="list-group-item text-center" key="empty">
                        <p className="lead">
                          <i className="fas fa-exclamation-circle text-secondary" />{' '}
                          {gt('No results found')}
                        </p>
                        <p>
                          {gt(
                            'The document or member was removed from the team. Or perhaps the team or sign off rules were deleted.'
                          )}
                        </p>
                      </li>
                    ) : (
                      activities.results.map((activity) => (
                        <li className="list-group-item" key={activity.id}>
                          <div className="font-weight-bold">
                            {getTriggerDescription(
                              activity.additional_data.triggered_by
                            )}
                          </div>
                          <div className="d-flex justify-content-between flex-wrap">
                            {activity.additional_data.team_ids != null &&
                              activity.additional_data.team_ids.map(
                                (team_id: number) => {
                                  const team = teamsMap[team_id];

                                  const teamContent =
                                    team == null ? (
                                      <span className="text-muted font-italic">
                                        {gt('Deleted')}
                                      </span>
                                    ) : (
                                      <Link
                                        to={getSignoffUrlForTeam(
                                          teamsMap[team_id]
                                        )}
                                      >
                                        {teamsMap[team_id].name}
                                      </Link>
                                    );

                                  return (
                                    <div key={team_id}>
                                      <i className="fa fa-users mr-1" />
                                      {gt('%s team', teamContent)}
                                    </div>
                                  );
                                }
                              )}
                            <ElapsedTime
                              timestamp={activity.timestamp}
                              ContainerTag="div"
                              className="text-secondary font-italic text-right"
                            />
                          </div>
                        </li>
                      ))
                    )
                  ) : (
                    <li className="list-group-item">
                      <p className="lead text-center mb-2 mt-3 text-danger">
                        <i className="fa fa-fw fa-exclamation-triangle" />
                      </p>
                      <p className="lead text-center">
                        {gt('Could not load sign off details at the moment')}
                      </p>
                    </li>
                  )}
                </ul>
              </div>

              <StyledModalFooter>
                <button className="btn btn-primary" onClick={onClose}>
                  {gt('Close')}
                </button>
              </StyledModalFooter>
            </>
          ),
        },
      ]}
    />
  );
}

function HeaderLine({
  className,
  label,
  children,
}: {
  className?: string;
  label?: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={'d-flex align-items-center ' + (className ?? '')}>
      <div style={{width: 100}} className="flex-shrink-0">
        {label}
      </div>
      <div className="font-weight-bold">{children}</div>
    </div>
  );
}

function getTriggerDescription(
  trigger: SignoffTriggerType | null | undefined
): string {
  switch (trigger) {
    case 'is_trigger_new_document':
      return gt('Document was added to team');
    case 'is_trigger_new_member':
      return gt('New member was added to team');
    case 'is_trigger_on_create':
      return gt('Sign off rules were created for team');
    case 'is_trigger_on_draft_approval':
      return gt('Document draft was approved');
    case 'is_trigger_on_approval':
      return gt('New document version was approved');
    case 'is_trigger_on_signoff_requested':
      return gt('Sign off was requested manually');
    default:
      return gt('Sign off requested');
  }
}

interface ButtonProps {
  signoff: SignoffSerializedType | SignoffDocumentSerializedType;
}

export function ButtonShowSignoffActivitiesModal({
  signoff,
}: ButtonProps): JSX.Element {
  const [modalIsOpen, setModalOpen] = React.useState(false);
  const [modalIsClosing, setModalIsClosing] = React.useState(false);

  return (
    <>
      <button
        className="btn btn-link btn-sm my-0 mr-0 p-0"
        style={{lineHeight: 1}}
        onClick={() => setModalOpen(true)}
      >
        <i className="far fa-info-circle" />
      </button>
      {modalIsOpen || modalIsClosing ? (
        <SignoffActivitiesModal
          signoffData={normalizeSignoffData(signoff)}
          isOpen={modalIsOpen}
          onClose={() => {
            setModalOpen(false);
            setModalIsClosing(true);
          }}
          onClosed={() => setModalIsClosing(false)}
        />
      ) : null}
    </>
  );
}
